import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { POST_URL } from '@utils/api/endpoints';
import { TokenExtension } from '@utils/extensions/token.extension';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface SwitchOrgParams {
  organizationId: string | number;
  orgName: string;
}

interface SwitchOrgResponse {
  result: {
    token: string;
  };
}

interface UseSwitchOrganizationOptions {
  // POST endpoint URL. Defaults to the SYSOP back-office endpoint so
  // existing callers behave unchanged. Reseller variant overrides this.
  endpoint?: string;
  // Where to land after switching. Defaults to '/'. Reseller flows may
  // prefer a reseller-scoped landing page.
  redirectTo?: string;
}

export function useSwitchOrganization(
  options: UseSwitchOrganizationOptions = {}
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const endpoint = options.endpoint ?? POST_URL().switchOrganization;
  const redirectTo = options.redirectTo ?? '/';

  return useMutation(
    async ({ organizationId }: SwitchOrgParams) => {
      const { data } = await Axios.post<SwitchOrgResponse>(endpoint, {
        organization_id: organizationId
      });
      return data;
    },
    {
      onSuccess: (data, variables) => {
        const token = data?.result?.token;
        if (!token) {
          toast.error(t('switch-org-failed'));
          return;
        }
        TokenExtension.set(token);
        toast.success(t('switched-to-org', { orgName: variables.orgName }));
        queryClient.clear();
        window.location.assign(redirectTo);
      },
      onError: () => {
        toast.error(t('switch-org-failed'));
      },
      useErrorBoundary: false
    }
  );
}
